import { type Health } from "#src/constants/health-types.ts";
import { type Mode } from "#src/constants/mode.ts";
import { type Concept, type Product } from "#src/types/Product.ts";

export interface RenderRules {
  incomeAmount?: boolean;
  faceAmount?: boolean;
  fixedFaceAmount?: boolean;
  premium?: boolean;
  monthlyBenefit?: boolean;
  benefitPeriod?: boolean;
  moneyPool?: boolean;
  mode?: boolean;
  payDuration?: boolean;
  guaranteedIssue?: boolean;
  selected_type?: boolean;
  subtype?: boolean;
  termLength?: boolean;
  livingBenefits?: boolean;
  state?: boolean;
  solve?: boolean;
  inflationPercentage?: boolean;
  eliminationPeriod?: boolean;
  homeHealthCareWaiver?: boolean;
  sharedCare?: boolean;
  jointIncome?: boolean;
  jointPremiumWaiver?: boolean;
  lapseProtectionToAge?: boolean;
  cashValue?: boolean;
  ageValue?: boolean;
  ltcRider?: boolean;
  ltcRiderPercentage?: boolean;
  partnerDiscount?: boolean;
  incomeSolve?: boolean;
  returnOfPremium?: boolean;
  discount?: boolean;
  incomeStartAge?: boolean;
  incomeEndAge?: boolean;
  creditingRate?: boolean;
  loanType?: boolean;
  section1035ExchangeAmount?: boolean;
  insuredHealth?: boolean;
  jointInsuredHealth?: boolean;
  saveAge?: boolean;
}

export interface QuoteParams {
  age_value?: number | null;
  benefit_period?: number | null;
  cash_value?: number | null;
  crediting_rate?: string | null;
  discount?: DiscountOption | null;
  elimination_period?: number | null;
  face_amount?: number | null;
  monthly_benefit?: number | null;
  partner_discount?: PartnerDiscount | null;
  joint_income?: boolean | null;
  filter_chronic_illness_and_ltc?: FilterChronicIllnessAndLtc | null;
  guaranteed_issue?: boolean | null;
  home_health_care_waiver?: boolean | null;
  income_amount?: number | null;
  income_end_age?: number | null;
  income_solve?: IncomeSolve | null;
  income_start_age?: number | null;
  inflation_percentage?: string | null;
  lapse_protection_to_age?: number | null;
  loan_type?: LoanType | null;
  living_benefits?: boolean | null;
  ltc_rider_percentage?: number | null;
  mode?: number | null;
  pool_of_money?: number | null;
  pay_duration?: number | null;
  premium?: number | null;
  return_of_premium_rider?: string | null;
  section_1035_exchange_amount?: number | null;
  save_age?: boolean | null;
  selected_type?: Product | Concept;
  subtype?: Product | null; // not stored in db
  shared_care?: boolean | null;
  joint_waiver_of_premium?: boolean | null;
  solve?: Solve | null;
  state?: string | null;
  active_years?: number | null;
  vitality_level_filter?: VitalityLevel | null;
  use_pool_of_money?: boolean | null;
}

// NOTE(tim): Gross, refactor
export interface ParsingData extends QuoteParams {
  age: number;
  initial_death_benefit?: number;
  section_1035_exchange?: {
    amount?: number;
  };
}

export interface QuoteRequestParams extends QuoteParams, InsuredQuoteParams {
  categories: string[] | ((arg: number) => string[]);
  compare: boolean;
  agent: {
    id: number;
  };
  joint_party: InsuredQuoteParams;
  section_1035_exchange?: {
    enabled?: boolean;
    amount?: number;
  };
}

export interface InsuredQuoteParams {
  birthdate: string;
  first_name: string;
  gender: string;
  height: number;
  last_name: string;
  weight: number;
  visa_type: string | null;
  health: Health;
  usages: string; // TODO(tim): define
  smoker: boolean;
  marital_status: string; // TODO(tim): define
}

export interface InsuredData {
  birthdate: { model: string };
  first_name: { model: string };
  gender: { model: string };
  height: { model: number };
  last_name: { model: string };
  weight: { model: number };
  visa_type: { model: string | null };
  health: { model: Health };
  smokerForRequests: {
    usages: string;
    smoker: boolean;
  };
  marital_status: { model: string };
}

export const SOLVE = {
  PAYOUT_AMOUNT: "Payout Amount",
  PREMIUM_REQUIRED: "Premium Required",
  BASE_PREMIUM: "Base Premium",
  LTC_BENEFIT: "LTC Benefit",
  BASE_DEATH_BENEFIT: "Base Death Benefit",
  LAPSE_PROTECTION_PREMIUM: "Lapse Protection Premium",
  MIN_NON_MEC_DEATH_BENEFIT: "Min Non-MEC DB",
  MAX_NON_MEC_PREMIUM: "Maximum Non-MEC Premium",
  TARGET_DEATH_BENEFIT: "Target Death Benefit",
  DEATH_BENEFIT_SOLVE_CASH_VALUE_AT_AGE: "DB Solve Cash Value at Age",
  SPECIFY_PREMIUM_AND_DEATH_BENEFIT: "Specify Premium and Death Benefit",
  TARGET_PREMIUM: "Target Premium",
  PREMIUM_SOLVE_CASH_VALUE_AT_AGE: "Premium Solve Cash Value at Age",
  LAPSE_PROTECTION_DEATH_BENEFIT: "Lapse Protection Death Benefit",
} as const;
export type Solve = (typeof SOLVE)[keyof typeof SOLVE];

export const INCOME_SOLVE = {
  NO: "No",
  MAX: "Max",
} as const;
export const INCOME_SOLVES = Object.values(INCOME_SOLVE);
export type IncomeSolve = (typeof INCOME_SOLVE)[keyof typeof INCOME_SOLVE];

export interface SectionedSolves {
  premium_solves?: SolveDataItem[];
  death_benefit_solves?: SolveDataItem[];
  no_solves?: SolveDataItem[];
}

export interface SolveDataItem {
  text: string;
  value: Solve;
  display?: string;
  displayQuantifier?: string;
}

export interface PartnerDiscountDataItem {
  text: string;
  value: PartnerDiscount;
  requiresJointInfo: PartnerDiscountRequirement;
}

export const RETURN_OF_PREMIUM_OPTIONS = [
  { text: "None", value: null, subtitle: "" },
  {
    text: "One Time STEP-Up",
    value: "One Time STEP-Up",
    subtitle: "Increases the surrender value.",
  },
  {
    text: "Vested",
    value: "Vested",
    subtitle: "Increases the surrender value at the fastest rate.",
  },
];

export interface ReturnOfPremiumDataItem {
  text: string;
  value: string | null;
  subtitle: string;
}

export interface LapseProtectionToAgeDataItem {
  text: string;
  value: string;
}

export interface PayDurationDataItem {
  title: string;
  value: number;
}

export interface AgeValueDataItem {
  text: string;
  value: number;
}

export interface BenefitPeriodDataItem {
  text: string;
  value: number;
}

export type ParamSuggestion = (age: number, params: QuoteParams) => string;

export interface LivingBenefitDataItem {
  text: string;
  value: boolean;
}

export interface DiscountDataItem {
  title: string;
  value: string | null;
}

export const INFLATION_OPTION = {
  NONE: null,
  COMPOUND_1: "1% Compound",
  COMPOUND_2: "2% Compound",
  COMPOUND_3: "3% Compound",
  SIMPLE_3: "3% Simple",
  COMPOUND_4: "4% Compound",
  COMPOUND_5: "5% Compound",
};

export function getInflationDataItem(
  option: InflationOption
): InflationDataItem {
  switch (option) {
    case INFLATION_OPTION.NONE: {
      return {
        value: INFLATION_OPTION.NONE,
        text: "None",
        decimalValue: 0,
      };
    }
    case INFLATION_OPTION.COMPOUND_1: {
      return {
        value: INFLATION_OPTION.COMPOUND_1,
        text: INFLATION_OPTION.COMPOUND_1,
        decimalValue: 0.01,
      };
    }
    case INFLATION_OPTION.COMPOUND_2: {
      return {
        value: INFLATION_OPTION.COMPOUND_2,
        text: INFLATION_OPTION.COMPOUND_2,
        decimalValue: 0.02,
      };
    }
    case INFLATION_OPTION.SIMPLE_3: {
      return {
        value: INFLATION_OPTION.SIMPLE_3,
        text: INFLATION_OPTION.SIMPLE_3,
        decimalValue: 0.03,
        simple: true,
      };
    }
    case INFLATION_OPTION.COMPOUND_3: {
      return {
        value: INFLATION_OPTION.COMPOUND_3,
        text: INFLATION_OPTION.COMPOUND_3,
        decimalValue: 0.03,
      };
    }
    case INFLATION_OPTION.COMPOUND_4: {
      return {
        value: INFLATION_OPTION.COMPOUND_4,
        text: INFLATION_OPTION.COMPOUND_4,
        decimalValue: 0.04,
      };
    }
    case INFLATION_OPTION.COMPOUND_5: {
      return {
        value: "5% Compound",
        text: "5% Compound",
        decimalValue: 0.05,
      };
    }

    default:
      throw `${option} is an unsupported inflation option type`;
  }
}

export type InflationOption =
  (typeof INFLATION_OPTION)[keyof typeof INFLATION_OPTION];

export interface InflationDataItem {
  value: InflationOption;
  text: string;
  decimalValue: number;
  simple?: boolean;
}

export const FILTER_CHRONIC_ILLNESS_AND_LTC = {
  WITHOUT_CHRONIC_LTC: "Without Chronic Illness/LTC Rider",
  WITH_LTC_RIDER: "With LTC Rider",
  WITH_CHRONIC_ILLNESS_LTC_RIDER: "With Chronic Illness/LTC Rider",
  WITH_FULL_CHRONIC_ILLNESS_LTC_RIDER: "With Full Chronic Illness/LTC Rider",
  WITH_CHRONIC_ILLNESS_RIDER: "With Chronic Illness Rider",
};

export const FILTER_CHRONIC_ILLNESS_AND_LTC_OPTIONS = Object.values(
  FILTER_CHRONIC_ILLNESS_AND_LTC
);

export type FilterChronicIllnessAndLtc =
  (typeof FILTER_CHRONIC_ILLNESS_AND_LTC)[keyof typeof FILTER_CHRONIC_ILLNESS_AND_LTC];

export const GUL_LAPSE_PROTECTION_TO_AGES = [120, 100, 95, 90, 85];
export const ACCIDENTAL_DEATH_RATES = [
  50000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000,
];
// numbers from 1,500 to 15,000, incremented by 500
const MIN_MONTHLY_BENEFIT = 2500;
export const MONTHLY_BENEFITS = new Array(
  (15000 - MIN_MONTHLY_BENEFIT) / 500 + 1
)
  .fill(0)
  .map((v, index) => MIN_MONTHLY_BENEFIT + index * 500);
export const MONTHLY_BENEFIT_MOST_COMMON = 5000;
// numbers from 75,000 to 500,000, incremented by 5,000

export const UNLIMITED_MONEY_POOL = -1;
const itemLength = (500000 - 75000) / 5000 + 1;
export const MONEY_POOL_OPTIONS = [
  ...new Array(itemLength).fill(0).map((v, index) => 75000 + index * 5000),
  UNLIMITED_MONEY_POOL,
];

export const ELIMINATION_PERIODS = [0, 30, 60, 90, 180, 365];
export const ELIMINATION_PERIOD_MOST_COMMON = 90;
export const BOOLEAN_OPTIONS = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

export const PARTNER_DISCOUNT_REQUIREMENTS = {
  NONE: null,
  VERBOSE: "verbose",
  BASIC: "basic",
};

export type PartnerDiscountRequirement =
  (typeof PARTNER_DISCOUNT_REQUIREMENTS)[keyof typeof PARTNER_DISCOUNT_REQUIREMENTS];

export const PARTNER_DISCOUNT = {
  JOINT: "both",
  SPOUSE: "one",
  SINGLE: null,
} as const;

export type PartnerDiscount =
  (typeof PARTNER_DISCOUNT)[keyof typeof PARTNER_DISCOUNT];

export const PARTNER_DISCOUNT_OPTIONS = [
  {
    text: "Single",
    value: PARTNER_DISCOUNT.SINGLE,
  },
  {
    text: "Married, Applying with Spouse",
    value: PARTNER_DISCOUNT.JOINT,
  },
  {
    text: "Married, Applying Individually",
    value: PARTNER_DISCOUNT.SPOUSE,
  },
];

export const RIDER_PERCENTAGE_OPTIONS = [1, 2, 3, 4];

export const MAX_INCOME_AGE = 121;

export const MIN_COL_SIZE = 4;

export const VITALITY_LEVEL = {
  NONE: "None",
  BRONZE: "Bronze",
  SILVER: "Silver",
  GOLD: "Gold",
  PLATINUM: "Platinum",
};

export type VitalityLevel =
  (typeof VITALITY_LEVEL)[keyof typeof VITALITY_LEVEL];

export interface VitalityLevelDataItem {
  text: string;
  value: VitalityLevel;
  color: string;
  matches: (v: string) => boolean;
}

export const VITALITY_LEVELS: VitalityLevelDataItem[] = Object.values(
  VITALITY_LEVEL
).map(level => ({
  text: level,
  value: level,
  color: `vitality-${level.toLowerCase()}`,
  matches: (v: string) => v === level,
}));

export const UNLIMITED_BENEFIT_PERIOD = -1;

export const DISCOUNT_OPTION = {
  NONE: null,
  ASSOCIATION: "Association",
  EMPLOYER: "Employer",
  PRODUCER: "Producer",
};

export type DiscountOption =
  (typeof DISCOUNT_OPTION)[keyof typeof DISCOUNT_OPTION];

export const DISCOUNT_OPTIONS: { text: string; value: DiscountOption }[] = [
  { text: "None", value: DISCOUNT_OPTION.NONE },
  { text: "Association", value: DISCOUNT_OPTION.ASSOCIATION },
  { text: "Employer", value: DISCOUNT_OPTION.EMPLOYER },
  { text: "Producer", value: DISCOUNT_OPTION.PRODUCER },
];

export const CREDITING_RATE = {
  DEFAULT_RATE: "Default Rate",
  REDUCTION_10: "10% Reduction",
  REDUCTION_20: "20% Reduction",
  REDUCTION_30: "30% Reduction",
  REDUCTION_40: "40% Reduction",
  REDUCTION_50: "50% Reduction",
};

export type CreditingRate =
  (typeof CREDITING_RATE)[keyof typeof CREDITING_RATE];

export type CreditingRateDataItem = { text: string; value: CreditingRate };
export const CREDITING_RATES: CreditingRateDataItem[] = [
  {
    value: CREDITING_RATE.DEFAULT_RATE,
    text: "Default Rate",
  },
  {
    value: CREDITING_RATE.REDUCTION_10,
    text: "10% Reduction",
  },
  {
    value: CREDITING_RATE.REDUCTION_20,
    text: "20% Reduction",
  },
  {
    value: CREDITING_RATE.REDUCTION_30,
    text: "30% Reduction",
  },
  {
    value: CREDITING_RATE.REDUCTION_40,
    text: "40% Reduction",
  },
  {
    value: CREDITING_RATE.REDUCTION_50,
    text: "50% Reduction",
  },
];

export const LOAN_TYPE = {
  FIXED: "Fixed",
  VARIABLE: "Variable",
};

export type LoanType = (typeof LOAN_TYPE)[keyof typeof LOAN_TYPE];

export interface LoanTypeDataItem {
  text: string;
  value: LoanType;
}

export const LOAN_TYPES: LoanTypeDataItem[] = [
  { text: "Fixed", value: "Fixed" },
  { text: "Participating", value: "Variable" },
];

export interface ModeDataItem {
  value: Mode;
  title: string;
}
export interface GuaranteedIssueDataItem {
  text: string;
  value: boolean | null;
}
export interface JointIncomeDataItem {
  title: string;
  value: boolean;
  dataTestid: string;
}

export const insuredQuoteData = (insured: InsuredData) => ({
  birthdate: insured.birthdate.model,
  first_name: insured.first_name.model,
  gender: insured.gender.model,
  height: insured.height.model,
  last_name: insured.last_name.model,
  weight: insured.weight.model,
  visa_type: insured.visa_type.model,
  health: insured.health.model,
  usages: insured.smokerForRequests.usages,
  smoker: insured.smokerForRequests.smoker,
  marital_status: insured.marital_status.model,
});
